import { combineReducers } from 'redux';
import { put, select, takeEvery } from 'redux-saga/effects';
import moment from 'moment';
import omitBy from 'lodash.omitby';
import findKey from 'lodash.findkey';
import get from 'lodash.get';
import notSet from '../utils/notSet';
import prepareBody from '../utils/prepareBody';
import getPageY from '../utils/getPageY';
import takeLatestFilter from '../utils/takeLatestFilter';
import { selectPlanId, selectRunnerId } from './auth';
import {
    workoutDayFromDate,
    getWorkoutDay,
    selectWorkout,
    fetchWorkout,
    FETCH_WORKOUT_SUCCESS,
    fetchWorkoutsData,
} from './workouts';
import { getRace } from './race';
import {
    runDistancesInPlanMi,
    runDistancesInPlan,
    dayOffTypes,
    runDistancesInPlanKm,
    findClosestDistance,
    buildRunDistances,
} from '../constants';

export const CHANGE_FIELD = 'log/CHANGE_FIELD';
export const OPEN_MODAL = 'log/OPEN_MODAL';
export const CLOSE_MODAL = 'log/CLOSE_MODAL';
export const SAVE_LOG = 'log/SAVE_LOG';
export const SAVE_LOG_SUCCESS = 'log/SAVE_LOG_SUCCESS';
export const SAVE_LOG_FAILURE = 'log/SAVE_LOG_FAILURE';
export const REMOVE_LOG = 'log/REMOVE_LOG';
export const DELETE_LOG = 'log/DELETE_LOG';
export const DELETE_LOG_SUCCESS = 'log/DELETE_LOG_SUCCESS';
export const DELETE_LOG_FAILURE = 'log/DELETE_LOG_FAILURE';
export const CLEAR_LOADING = 'log/CLEAR_LOADING';
export const SET_ERROR = 'log/SET_ERROR';
export const CLEAR_ALL_ERRORS = 'log/CLEAR_ALL_ERRORS';

const readStrava = (strava) => {
    if (strava && strava.id) {
        const timeInSeconds = strava.time ? Number(strava.time) : 0;
        const hours = Math.floor(timeInSeconds / (60 * 60));
        const minutes = Math.floor(timeInSeconds / 60) % 60;
        const seconds = Math.floor(timeInSeconds % 60);

        return {
            log_miles: strava.distance,
            log_hr: (hours && hours !== 0) ? hours.toString() : '',
            log_min: (minutes && minutes !== 0) ? minutes.toString() : '',
            log_sec: (seconds && seconds !== 0) ? seconds.toString() : '',
            log_splits: strava.notes,
            strava: true,
        };
    }
    return {};
};

export function fields(state = {}, action) {
    switch (action.type) {
        case SAVE_LOG:
        case REMOVE_LOG:
        case CLOSE_MODAL:
            return {};
        case CHANGE_FIELD:
            return {
                ...state,
                [action.key]: action.val,
            };
        case OPEN_MODAL: {
            if (!action.workout) {
                return {};
            }
            let comments = '';
            let stravaSplits = '';
            if (action.workout.completed && action.workout.strava && action.workout.strava.id) {
                const whole = action.workout.log.log_comments;
                // work with old and new log entries
                const parts = whole.split('Name:');
                comments = parts[0].trim();
                stravaSplits = `Name:${parts[1]}`;
            } else if (action.workout.log && action.workout.log.log_comments) {
                comments = action.workout.log.log_comments;
            }
            return {
                log_runtype: '',
                log_miles: action.workout.volume || '',
                date: action.workout.date,
                log_hr: '',
                log_min: '',
                log_sec: '',
                log_rpe: '',
                log_ecoach: 0,
                log_strides: action.workout.strides,
                log_rt: action.workout.resistanceTraining,
                race_name: get(action, 'workout.raceName', ''),
                log_wuwd_miles: action.workout.wuwdVolume || '',
                log_updaterace: action.workout.log_updaterace || 0,
                log_splits: stravaSplits,
                ...readStrava(action.strava),
                ...omitBy(action.workout.log, notSet),
                log_comments: comments,
            };
        }
        default:
            return state;
    }
}

export function open(state = false, action) {
    switch (action.type) {
        case OPEN_MODAL:
            return true;
        case SAVE_LOG:
        case REMOVE_LOG:
        case CLOSE_MODAL:
        case DELETE_LOG:
            return false;
        default:
            return state;
    }
}

export function loading(state = false, action) {
    switch (action.type) {
        case CHANGE_FIELD: {
            if (action.key !== 'date') {
                return state;
            }
            return true;
        }
        case CLOSE_MODAL:
        case CLEAR_LOADING:
            return false;
        default:
            return state;
    }
}

export function opened(state = {}, action) {
    if (action.type === OPEN_MODAL) {
        return {
            [action.workout.day]: true,
        };
    }
    return state;
}

export function errors(state = {}, action) {
    switch (action.type) {
        case CHANGE_FIELD: {
            return {
                ...state,
                [action.key]: null,
            };
        }
        case SET_ERROR:
            return {
                ...state,
                [action.key]: action.error,
            };
        case CLOSE_MODAL:
        case CLEAR_ALL_ERRORS:
        case SAVE_LOG_SUCCESS:
            return {};
        default:
            return state;
    }
}

export function yPosition(state = null, action) {
    switch (action.type) {
        case OPEN_MODAL:
            return action.yPos;
        default:
            return state;
    }
}

export function deleting(state = false, action) {
    switch (action.type) {
        case DELETE_LOG:
            return true;
        case DELETE_LOG_SUCCESS:
        case DELETE_LOG_FAILURE:
            return false;
        default:
            return state;
    }
}

export default combineReducers({
    fields,
    yPosition,
    open,
    loading,
    errors,
    opened,
    deleting,
});


export function* getWorkout(action) {
    if (action.key === 'log_hr'
        || action.key === 'log_min'
        || action.key === 'log_sec') {
        yield put(setError('timeError', null));
    }
    if (action.key !== 'date') {
        return null;
    }
    const { race, workouts, log } = yield select();
    const totalWorkouts = race.info.plan_week * 7;
    const raceDate = race.info.race_date;
    const treningDay = workoutDayFromDate({
        date: action.val,
        totalWorkouts,
        raceDate,
    });
    let workout = selectWorkout(workouts, treningDay);
    if (!workout) {
        const workoutPromise = yield put(
            fetchWorkout(treningDay),
        );
        yield workoutPromise;
        const state = yield select();
        workout = selectWorkout(state.workouts, treningDay);
    }
    const workoutLogId = get(workout, 'log.log_id', undefined);
    if (workoutLogId && log.fields.log_id !== workout.log.log_id) {
        yield put(setError('date', 'You’ve already logged a workout for this day'));
    }
    return yield put({ type: CLEAR_LOADING });
}

export function* refreshPlanAndWorkout({ log }) {
    const { race } = yield select();
    const totalWorkouts = race.info.plan_week * 7;
    const raceDate = race.info.race_date;
    const treningDay = workoutDayFromDate({
        date: log.date,
        totalWorkouts,
        raceDate,
    });
    return yield [
        put(fetchWorkout(treningDay)),
        put(getRace()),
        put(fetchWorkoutsData()),
    ];
}

export function* changeRaceLogMiles(action) {
    if (action.key === 'log_runtype' && action.val === 'Race') {
        const { race, log } = yield select();
        const units = race.info.units;
        const oldMiles = log.fields.log_miles;
        const newMiles = oldMiles ? findClosestDistance(oldMiles, buildRunDistances(units)) : '';
        return yield put(changeField('log_miles', newMiles));
    }
    return null;
}

export function* watchChange() {
    yield takeEvery(CHANGE_FIELD, changeRaceLogMiles);
    yield takeLatestFilter(CHANGE_FIELD, ({ key }) => key, getWorkout);
}

export function* watchEditSave() {
    yield takeEvery([SAVE_LOG_SUCCESS, DELETE_LOG_SUCCESS], refreshPlanAndWorkout);
}

export function* watchGetWorkout() {
    yield takeEvery(FETCH_WORKOUT_SUCCESS, openLogModalOnPlanCompleted);
}

// it automatically pops up once the date is past the goal race date
export function* openLogModalOnPlanCompleted(action) {
    const workout = action.result;
    const { race, log, auth } = yield select();
    if (!auth.planId) {
        return;
    }

    if (workout.strava && workout.strava.id && !workout.completed && action.fromFullPlan) {
        yield put(openModal(workout, { strava: workout.strava }));
    }

    const lastDay = race.info.plan_week * 7;

    // get workout day will return last workout if
    // workout is pass the current day
    const today = getWorkoutDay({
        raceDate: race.info.race_date,
        totalWorkouts: race.info.plan_week * 7,
    });

    // do not open again same modal
    if (log.opened[workout.day]) {
        return;
    }
    // date not passed
    if (lastDay !== today) {
        return;
    }

    // not goal race
    if (lastDay !== workout.day) {
        return;
    }

    // ignore logged workout
    if (workout.completed) {
        return;
    }

    yield put(openModal(workout));
}

export function* rootSaga() {
    yield [
        watchChange(),
        watchEditSave(),
        watchGetWorkout(),
    ];
}

export const changeField = (key, val) => ({
    type: CHANGE_FIELD,
    key,
    val,
});

export const setError = (key, error) => ({
    type: SET_ERROR,
    key,
    error,
});

export const openModal = (workout = {}, options = {}) => {
    const y = getPageY();

    return {
        type: OPEN_MODAL,
        workout,
        ...options,
        yPos: y,
    };
};

export const closeModal = () => ({
    type: CLOSE_MODAL,
});

export const clearAllErrors = () => ({
    type: CLEAR_ALL_ERRORS,
});

export const deleteWorkout = (log, extra = {}) => ({
    types: [DELETE_LOG, DELETE_LOG_SUCCESS, DELETE_LOG_FAILURE],
    promise: ({ getState }) => fetch(`${process.env.RTAPI_URL}/API_delete.php`, {
        method: 'POST',
        body: prepareBody({
            plan_id: selectPlanId(getState()),
            runner_id: selectRunnerId(getState()),
            log_id: log.log_id,
        }),
    }).then(res => res.json()),
    log: {
        ...log,
        ...extra,
    },
});

export const saveLog = (log, extra = {}) => {
    const copiedLog = Object.assign({}, log);
    if (extra.goal) {
        copiedLog.log_runtype = 'Race';
    }
    if (!log.race_name) {
        delete copiedLog.race_name;
    }
    if (log.log_splits) {
        if (log.log_comments) {
            copiedLog.log_comments = `${copiedLog.log_comments}\n\n${copiedLog.log_splits}`;
        } else {
            copiedLog.log_comments = copiedLog.log_splits;
        }
    }
    if (log.date) {
        copiedLog.date = moment(copiedLog.date).format('YYYY-MM-DD');
    }
    if (dayOffTypes.includes(log.log_runtype)) {
        copiedLog.log_miles = 0;
    }
    const url = copiedLog.log_id ?
        `${process.env.RTAPI_URL}/API_edit.php` :
        `${process.env.RTAPI_URL}/API_add.php`;

    return {
        types: [SAVE_LOG, SAVE_LOG_SUCCESS, SAVE_LOG_FAILURE],
        promise: ({ getState }) => {
            const state = getState();
            const { race } = state;
            const totalWorkouts = race.info.plan_week * 7;
            const raceDate = race.info.race_date;
            const day = workoutDayFromDate({
                date: log.date,
                totalWorkouts,
                raceDate,
            });
            const runDistancesForUser = race.info.units === 'Metric' ? runDistancesInPlanKm : runDistancesInPlanMi;
            if (log.log_runtype === 'Race') {
                // marathon distance (which should only be an option for goal race), set manually
                // To show marathon in dropdown for in-plan race, add it to runDistancesInPlan
                if (Number(log.log_miles) === (26.2 || 42.2)) {
                    copiedLog.log_miles = 42195;
                } else {
                    const closestRaceDist = findClosestDistance(log.log_miles, runDistancesForUser);
                    const raceDistanceString = runDistancesForUser[closestRaceDist];
                    copiedLog.log_miles = findKey(runDistancesInPlan,
                        item => (raceDistanceString === item));
                }
            }
            const workout = state.workouts.items[day];
            return (fetch(url, {
                method: 'POST',
                body: prepareBody({
                    plan_id: selectPlanId(getState()),
                    runner_id: selectRunnerId(getState()),
                    ...copiedLog,
                    ...extra,
                    prescribed_type: workout.type,
                    prescribed_miles: workout.volume,
                }),
            }).then(res => res.json()));
        },
        log: {
            ...copiedLog,
            ...extra,
        },
    };
};



/** WEBPACK FOOTER **
 ** ./src/redux/log.js
 **/